import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import StructuredData from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import confetti from "canvas-confetti";

const NumeroSuerte = () => {
  const [numbers, setNumbers] = useState<string[]>(["--", "--", "--", "--", "--"]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const generateUniqueNumbers = () => {
    const uniqueNumbers = new Set<string>();
    while (uniqueNumbers.size < 5) {
      const num = Math.floor(Math.random() * 100).toString().padStart(2, '0');
      uniqueNumbers.add(num);
    }
    return Array.from(uniqueNumbers);
  };

  const triggerConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Disparar desde dos lados
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const startGeneration = () => {
    setIsGenerating(true);
    setCurrentIndex(0);
    setNumbers(["--", "--", "--", "--", "--"]);
  };

  useEffect(() => {
    if (isGenerating && currentIndex === 0) {
      // Generate all unique numbers at the start
      const uniqueNumbers = generateUniqueNumbers();
      
      // Animate each number one by one
      const animateNumber = (index: number) => {
        if (index >= 5) {
          setIsGenerating(false);
          // Disparar confetti cuando se completan todos los números
          triggerConfetti();
          return;
        }

        const interval = setInterval(() => {
          setNumbers(prev => {
            const newNumbers = [...prev];
            newNumbers[index] = Math.floor(Math.random() * 100).toString().padStart(2, '0');
            return newNumbers;
          });
        }, 50);

        setTimeout(() => {
          clearInterval(interval);
          setNumbers(prev => {
            const newNumbers = [...prev];
            newNumbers[index] = uniqueNumbers[index] ?? "";
            return newNumbers;
          });
          setCurrentIndex(index + 1);
          
          // Pausa entre cada número para crear suspense
          setTimeout(() => {
            animateNumber(index + 1);
          }, 300);
        }, 1500);
      };

      animateNumber(0);
    }
  }, [isGenerating]);

  return (
    <>
      <SEOHead
        title="Tus Números de la Suerte - Genera Números Aleatorios | TiemposHoy"
        description="Genera tus números de la suerte para jugar en la lotería. Obtén 5 números aleatorios únicos del 00 al 99 para Nicaragua y Costa Rica."
        keywords="números de la suerte, números aleatorios, lotería, sorteo, nica, tica"
        canonicalUrl="https://tiemposhoy.com/numero-suerte"
      />
      <StructuredData
        title="Generador de Números de la Suerte"
        description="Genera tus números de la suerte para jugar en la lotería de Nicaragua y Costa Rica"
      />
      
      <div className="min-h-screen flex flex-col">
        <Navigation />
        
        <main className="flex-1 container mx-auto px-4 py-8 flex flex-col items-center justify-center">
          <header className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-nicaragua via-costa-rica to-accent bg-clip-text text-transparent">
              ✨ Tus Números de la Suerte
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              La suerte te encuentra donde menos lo esperas
            </p>
          </header>

          <div className="w-full max-w-4xl mb-12">
            {/* Grid responsive: 3 columnas en móvil (primera fila), 2 en segunda fila, 5 en desktop */}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-6 mb-8">
              {numbers.map((num, index) => (
                <div
                  key={index}
                  className={`aspect-square flex items-center justify-center rounded-xl border-2 transition-all duration-500 ${
                    index === currentIndex && isGenerating
                      ? "border-accent bg-accent/10 scale-110 shadow-lg animate-pulse"
                      : index < currentIndex
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-border bg-background"
                  } ${index >= 3 ? 'md:col-span-1' : ''} ${index === 3 ? 'col-start-1' : ''} ${index === 4 ? 'col-start-2' : ''}`}
                >
                  <span className={`text-3xl md:text-5xl lg:text-6xl font-bold transition-all duration-300 ${
                    num === "--" ? "text-muted-foreground" : "text-primary"
                  }`}>
                    {num}
                  </span>
                </div>
              ))}
            </div>

            <div className="text-center space-y-4">
              {!isGenerating && numbers[0] !== "--" && (
                <p className="text-xl md:text-2xl font-bold text-primary animate-fade-in">
                  ✨ ¡Estos son tus números de la suerte! ✨
                </p>
              )}
              <Button
                onClick={startGeneration}
                disabled={isGenerating}
                size="lg"
                className="text-lg px-8 py-6 gap-3 transition-all duration-300"
              >
                <Sparkles className="h-6 w-6" />
                {isGenerating ? "Generando..." : numbers[0] === "--" ? "Generar Números" : "Generar Nuevos Números"}
              </Button>
            </div>
          </div>

          <div className="text-center max-w-2xl text-muted-foreground">
            <p className="mb-4">
              Estos números son completamente aleatorios y generados para darte un poco de inspiración y diversión.
            </p>
            <p className="text-sm">
              Recuerda jugar responsablemente. Los juegos de azar son para entretenimiento.
            </p>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default NumeroSuerte;
